import {Archive, BookOpenText, GitFork, Github, Newspaper, Star} from 'lucide-react';
import {memo, ReactElement} from 'react';
import LanguagesStrip from './LanguagesStrip';
import ProjectDescription from './ProjectDescription';
import {FeaturedProject} from 'types';
import {getLanguages} from 'utils';

type FeaturedProjectCardProps = {
  project: FeaturedProject;
};

type FeaturedProjectRepo = FeaturedProject['repo'];

type ProjectStatsProps = {
  hasRepoStats: boolean;
  isBlogPost: boolean;
  repo: FeaturedProjectRepo;
};

type ProjectLinksProps = {
  blogUrl?: string;
  codeUrl?: string;
  project: FeaturedProject;
};

function ProjectKindIcon({isBlogPost}: { isBlogPost: boolean }): ReactElement {
  return isBlogPost ? <Newspaper aria-hidden="true" /> : <Github aria-hidden="true" />;
}

function ProjectStats({hasRepoStats, isBlogPost, repo}: ProjectStatsProps): ReactElement {
  return (
    <div className="repo-stats">
      {isBlogPost && (
        <span>
          <Newspaper aria-hidden="true" />
          Writeup
        </span>
      )}

      {hasRepoStats && repo?.stargazerCount ? (
        <span aria-label={`${repo.stargazerCount} GitHub stars`}>
          <Star aria-hidden="true" />
          {repo.stargazerCount}
        </span>
      ) : null}

      {hasRepoStats && repo?.forkCount ? (
        <span aria-label={`${repo.forkCount} GitHub forks`}>
          <GitFork aria-hidden="true" />
          {repo.forkCount}
        </span>
      ) : null}

      {hasRepoStats && repo?.isArchived && (
        <span className="archived-text">
          <Archive aria-hidden="true" />
          Archived
        </span>
      )}
    </div>
  );
}

function ProjectLinks({blogUrl, codeUrl, project}: ProjectLinksProps): ReactElement {
  return (
    <div className="repo-links">
      {blogUrl && (
        <a className="icon-button" aria-label={`Read ${project.title} writeup`} href={blogUrl}>
          <BookOpenText aria-hidden="true" />
        </a>
      )}

      {codeUrl && (
        <a
          className="icon-button"
          aria-label={`Open ${project.title} code`}
          href={codeUrl}
        >
          <Github aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

function FeaturedProjectCard({project}: FeaturedProjectCardProps): ReactElement {
  const repo = project.repo;
  const blogUrl = project.blogUrl || undefined;
  const configuredCodeUrl = project.codeUrl || undefined;
  const writeupUrl = project.blogUrl || project.codeUrl;
  const primaryUrl = project.isBlogPost ? writeupUrl : project.codeUrl || repo?.url;
  const codeUrl = project.isBlogPost ? configuredCodeUrl : configuredCodeUrl || repo?.url;
  const hasRepoStats = !project.isBlogPost && Boolean(
    repo && (repo.stargazerCount > 0 || repo.forkCount > 0 || repo.isArchived)
  );

  return (
    <div className="repo-card-root">
      <article className="repo-card featured-project-card">
        {primaryUrl && (
          <a className="repo-card-overlay" href={primaryUrl} aria-label={`Open ${project.title}`} />
        )}
        <LanguagesStrip data={project.languages ?? (repo ? getLanguages(repo) : [])} />
        <div className="content">
          <div className="content-header">
            <div className="icon-root">
              <ProjectKindIcon isBlogPost={project.isBlogPost} />
            </div>
            <div className="title-root">
              {primaryUrl ? (
                <a href={primaryUrl} className="title">{project.title}</a>
              ) : (
                <span className="title">{project.title}</span>
              )}
              <p className="subtitle">{project.eyebrow}</p>
            </div>
          </div>

          <ProjectDescription
            description={project.description}
            fallback="A project from my archive."
            repo={repo}
          />

          <div className="topics">
            {project.tags.map((tag) => <span key={tag} className="topic">{tag}</span>)}
          </div>

          <div className="content-footer">
            <ProjectStats hasRepoStats={hasRepoStats} isBlogPost={project.isBlogPost} repo={repo} />
            <ProjectLinks blogUrl={blogUrl} codeUrl={codeUrl} project={project} />
          </div>
        </div>
      </article>
    </div>
  );
}

export default memo(FeaturedProjectCard);
